import React, {useContext, useEffect, useMemo, useState} from "react";
import {useAuth, UserContext} from "../../context/user";
import ClientSelect from "../../components/form-v2/searchable-select/ClientSelect";
import DepartmentSelect from "../../components/form-v2/searchable-select/DepartmentSelect";
import UserSelect from "../../components/form-v2/searchable-select/UserSelect";
import CountrySelect from "../../components/form-v2/searchable-select/CountrySelect";
import BasicText from "../../components/form-v2/basic-text";
import {BlueButton, BlueButtonCentered} from "../../components/ui/Buttons";
import searchIcon from "../../assets/icons/icon-search-light.svg";
import {Client, Department} from "../../types/userManagement";
import {UserDataTypes} from "../../types/user-data-types";
import {CountryType} from "../../constants/countries";
import moment from "moment-timezone";
import {apiGET} from "../../apiv2/request";
import {apiV2} from "../../api/axiosConfig";
import * as XLSX from "xlsx";
import {Itinerary} from "../TripsV2/types";
import {dateToString, dateToStringTZ} from "../../util/helpers";
import {useNavigate, useSearchParams} from "react-router-dom";
import Pagination from "../../components/ui/Content/Pagination";
import {Action, ActionSelect} from "../../components/ui/ActionSelect";
import {CountryRiskProvider, useCountryRisk} from "../../context/countryrisk";
import {startCase} from "lodash";
import WorldSearchTypeSelect, {WorldSearchType} from "../../components/form-v2/searchable-select/WorldSearchTypeSelect";
import {RiskLevelSelect} from "../../components/form-v2/searchable-select/RiskLevelSelect";
import {useTimeZoneSelectContext} from "../../providers/TimeZoneSelectProvider";
import {MassWarningModal} from "./components/MassWarningModal";
import {PaginatedResponseV2} from "../../types/PaginatedResponse";
import {usePermissions} from "@/context/modulePermissions";
import {useClientModules} from "@/context/clientModules";


function WorldSearch() {


    const [client, setClient] = useState<Client | undefined>(undefined)
    const [department, setDepartment] = useState<Department | undefined>(undefined)
    const [users, setUsers] = useState<UserDataTypes[] | undefined>(undefined)
    const [countries, setCountries] = useState<CountryType[] | undefined>(undefined)
    const [minRiskLevel, setMinRiskLevel] = useState<number | undefined>()

    const [query, setQuery] = useState<string>("")

    const [errorMessage, setErrorMessage] = useState("");
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(false)

    const [records, setRecords] = useState<PaginatedResponseV2<Item> | undefined>()
    const [page, setPage] = useState(1)
    const [rpp, setRPP] = useState(10)

    const [queryParams] = useSearchParams()
    const tripID = queryParams.get("tripID")
    const userID = queryParams.get("userID")
    const deviceID = queryParams.get("deviceID")

    const [startDate, setStartDate] = useState<string>(queryParams.get("startDate") || moment().format("YYYY-MM-DD"))
    const [endDate, setEndDate] = useState<string>(queryParams.get("endDate") || moment().add({month: 1}).format("YYYY-MM-DD"))


    const [downloading, setDownloading] = useState(false)

    const [prevParams, setPrevParams] = useState<Map<string, string> | undefined>()
    const {isSuperAdmin} = useAuth();
    const {hasFullAccess} = usePermissions();
    const {hasModule} = useClientModules()


    const defaultTypes: WorldSearchType[] = deviceID
        ? [{label: "Satellite - Location", value: "satellite:location"}]
        : [
            {label: "Trip - Travel", value: "trip:travel"},
            {label: "Trip - Accommodation", value: "trip:accommodation"},
            {label: "App - Check In", value: "app:check_in"},
            {label: "App - SMS Check In", value: "app_sms:check_in"},
            {label: "App - Emergency Alarm", value: "app:emergency_alarm"},
            {label: "App - SMS Emergency Alarm", value: "app_sms:emergency_alarm"},
            {label: "App - Silent Alarm", value: "app:silent_alarm"},
            {label: "App - SMS Silent Alarm", value: "app_sms:silent_alarm"},
        ];


    const [types, setTypes] = useState<WorldSearchType[]>(defaultTypes);


    const [showMassWaring, setShowMassWaring] = useState(false)

    const {role} = useContext(UserContext)!;
    const tzContext = useTimeZoneSelectContext()


    useEffect(() => {
        load()
    }, [page]);

    const [currentParams, setCurrentParams] = useState<Record<string, string>>({})

    const getParams = (): Map<string, string> => {
        const params = new Map<string, string>()
        if (!tripID) {
            params.set("page", String(page))
            params.set("limit", String(rpp))

            if (client) {
                params.set("clientID", client.id as string)
                params.set("clientID", client.id as string)
            }

            if (department) {
                params.set("departmentID", department.id)
            }

            if (userID) {
                params.set("userID", userID)
            }

            if (deviceID) {
                params.set("deviceID", deviceID)
            }

            if (users) {
                params.set("userIDs", users.map((u) => u.id).join(","))
            }

            if (countries) {
                params.set("countryISOs", countries.map(c => c.code).join(","))
            }

            if (startDate && endDate) {
                params.set("startDate", startDate)
                params.set("endDate", endDate)
            }

            if (types && types.length > 0) {
                const setTypes = new Set(types.map(t => t.value))
                if (setTypes.has("trip:travel")) {
                    setTypes.delete("trip:travel")
                    setTypes.add("travel:flight")
                    setTypes.add("travel:taxi")
                    setTypes.add("travel:ferry")
                    setTypes.add("travel:train")
                    setTypes.add("travel:rental")
                    setTypes.add("travel:private_car")
                    setTypes.add("travel:bus")
                    setTypes.add("travel:other")
                    setTypes.add("travel:undefined")
                }

                if (setTypes.has("trip:accommodation")) {
                    setTypes.delete("trip:accommodation")
                    setTypes.add("accommodation:hotel")
                    setTypes.add("accommodation:private_property")
                    setTypes.add("accommodation:home")
                    setTypes.add("accommodation:other")
                    setTypes.add("accommodation:undefined")

                }

                if (setTypes.has("trip:scheduled_check_in")) {
                    setTypes.delete("trip:scheduled_check_in")
                    setTypes.add("event:scheduled_check_in")
                }

                params.set("subtypes", Array.from(setTypes).join(","))
            }

            if (query) {
                params.set("query", query)
            }

            if (minRiskLevel) {
                params.set("minimumRiskLevel", String(minRiskLevel))
            }

        } else {
            params.set("tripID", tripID)
        }

        return params
    }


    const load = async () => {
        setError(false)
        setLoading(true)

        try {
            const params = getParams()
            setCurrentParams(Object.fromEntries(params))


            setPrevParams(params)


            const results = await apiGET<any>(`${process.env.REACT_APP_API_V2_URL}/world-search`, Object.fromEntries(params), {
                "Aurora-Time-Zone": localStorage.getItem("auroraTimeZone") || "UTC",
            })
            setRecords(results)

        } catch (e) {
            setError(true)
        }
        setLoading(false)

    }

    const download = async () => {
        setDownloading(true)
        try {
            const baseParams = getParams()
            baseParams.delete("page")
            baseParams.delete("limit")

            const pageSize = 100
            let currentPage = 1
            let totalPages = 1
            const allItems: Item[] = []

            do {
                const params = new Map(baseParams)
                params.set("page", String(currentPage))
                params.set("limit", String(pageSize))

                const result = await apiV2.get<PaginatedResponseV2<Item>>("/world-search", {
                    params: Object.fromEntries(params),
                })

                const data = result.data
                allItems.push(...data.results)
                totalPages = data.totalPages ?? totalPages
                currentPage++
            } while (currentPage <= totalPages)

            const typeLabels: Record<string, string> = {
                "travel": "Trip",
                "accommodation": "Trip",
                "event": "Trip",
                "app": "App",
                "app_sms": "App SMS",
                "satellite": "Satellite",
            }
            const subtypeLabels: Record<string, string> = {
                "travel:flight": "Travel - Flight",
                "travel:taxi": "Travel - Taxi",
                "travel:ferry": "Travel - Ferry",
                "travel:train": "Travel - Train",
                "travel:rental": "Travel - Rental",
                "travel:private_car": "Travel - Private Car",
                "travel:bus": "Travel - Bus",
                "travel:other": "Travel - Other",
                "travel:undefined": "Travel",
                "accommodation:hotel": "Accommodation - Hotel",
                "accommodation:private_property": "Accommodation - Private Property",
                "accommodation:home": "Accommodation - Home",
                "accommodation:other": "Accommodation - Other",
                "accommodation:undefined": "Accommodation",
                "event:scheduled_check_in": "Scheduled Check In",
                "app:check_in": "Check In",
                "app_sms:check_in": "Check In",
                "app:emergency_alarm": "Emergency Alarm",
                "app_sms:emergency_alarm": "Emergency Alarm",
                "app:silent_alarm": "Silent Alarm",
                "app_sms:silent_alarm": "Silent Alarm",
                "satellite:location": "Location",
            }

            const rows = allItems.map((item) => {
                const source = item.subtype.split(":")[0]
                const type = typeLabels[source] ?? source.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
                const subtype = subtypeLabels[item.subtype] ?? item.subtype.split(":")[1]?.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()) ?? ""
                return {
                    "Type": type,
                    "Subtype": subtype,
                    "Client": item.clientName,
                    "Department": item.departmentName,
                    "User": item.userName,
                    "Start Time": moment.tz(item.startTime, tzContext.timeZone).format("YYYY-MM-DD HH:mm:ss Z z"),
                    "End Time": moment.tz(item.endTime, tzContext.timeZone).format("YYYY-MM-DD HH:mm:ss Z z"),
                    "Address Lines": item.location.addressLines.join(", "),
                    "Town": item.location.town,
                    "County": item.location.county,
                    "Region": item.location.region,
                    "Nearest Place": item.location.nearestPlace,
                    "Country": item.location.countryName,
                    "Country Code": item.location.countryISO,
                    "Timezone": item.location.timezone,
                    "Latitude": item.location.point?.coordinates[1] != null ? parseFloat(item.location.point.coordinates[1].toFixed(5)) : undefined,
                    "Longitude": item.location.point?.coordinates[0] != null ? parseFloat(item.location.point.coordinates[0].toFixed(5)) : undefined,
                    "Country Risk Level": item.currentCountryRisk,
                }
            })

            const ws = XLSX.utils.json_to_sheet(rows)
            ws["!cols"] = [
                {wch: 16}, // Type
                {wch: 22}, // Subtype
                {wch: 30}, // Client
                {wch: 25}, // Department
                {wch: 28}, // User
                {wch: 28}, // Start Time
                {wch: 28}, // End Time
                {wch: 35}, // Address Lines
                {wch: 18}, // Town
                {wch: 18}, // County
                {wch: 20}, // Region
                {wch: 20}, // Nearest Place
                {wch: 20}, // Country
                {wch: 14}, // Country Code
                {wch: 22}, // Timezone
                {wch: 12}, // Latitude
                {wch: 12}, // Longitude
                {wch: 18}, // Country Risk Level
            ]
            const wb = XLSX.utils.book_new()
            XLSX.utils.book_append_sheet(wb, ws, "World Search")
            XLSX.writeFile(wb, `world-search-${Date.now()}.xlsx`)
        } catch (e) {
            console.error(e)
        }
        setDownloading(false)
    }

    const showSiren = useMemo(() => {
        if (!hasModule("siren")) return false;
        if (!hasFullAccess("siren_alert")) return false;
        if (isSuperAdmin && !currentParams.clientID) return false;
        if (records?.totalResults != 0) return true

    }, [isSuperAdmin, hasFullAccess, hasModule, currentParams, records])


    return (

        <CountryRiskProvider>

            <MassWarningModal show={showMassWaring} setShow={setShowMassWaring} params={currentParams}
                              clientID={client?.id}/>

            <div className="h-full pt-36 pb-16 px-7">
                {!tripID &&
                    <div className="mt-5 shadow-lg px-6 pt-7 pb-8 relative bg-white">
                        <div className="flex items-center justify-between py-4 border-b text-md mb-4">
                            <span className="font-semibold text-lg">Filters</span>
                        </div>
                        <div className={"grid grid-cols-4 gap-4"}>
                            {["all"].includes(role.auroraAccessLevel) &&
                                <ClientSelect clients={client ? [client] : []}
                                              onSelectionChange={(clients) => setClient(clients[0])}/>
                            }
                            {["all", "client", "department"].includes(role.auroraAccessLevel) &&
                                <DepartmentSelect departments={department ? [department] : []} clientID={client?.id}
                                                  onSelectionChange={(departments) => setDepartment(departments[0])}/>
                            }
                            <UserSelect users={users ? users : []} clientID={client?.id} departmentID={department?.id}
                                        onSelectionChange={(users) => setUsers(users)} multiple={true}
                                        disabled={!!userID}
                                        initialUserIDs={userID ? [userID] : []}/>
                            <CountrySelect countries={countries ? countries : []}
                                           onSelectionChange={(c) => setCountries(c)}
                                           multiple={true}/>
                            <BasicText value={startDate} type={"date"} onChange={setStartDate}
                                       placeholder={"Start Date"}/>
                            <BasicText value={endDate} type={"date"} onChange={setEndDate} placeholder={"End Date"}/>
                            <BasicText value={query} onChange={setQuery} placeholder={"Search"}/>

                            <WorldSearchTypeSelect worldSearchTypes={types} onSelectionChange={(v) => setTypes(v)}
                                                   multiple={true}/>
                            <RiskLevelSelect value={minRiskLevel} setValue={setMinRiskLevel}/>
                            <BlueButtonCentered text={"Filter"} icon={searchIcon} onClick={() => {
                                setPage(1);
                                load()
                            }}/>
                        </div>
                    </div>
                }

                <div className="mt-5 shadow-lg px-6 pt-7 pb-20 relative bg-white">
                    <div className="flex items-center justify-between py-4 border-b text-md mb-4">
                        <span className="font-semibold text-lg">World Search</span>
                        <div className={"flex gap-4"}>
                            <div className={"w-48"}>
                                {currentParams.clientID && records?.totalResults != 0 &&
                                    <BlueButton text={"Send Siren Alert"} onClick={() => setShowMassWaring(true)}/>}
                            </div>
                            <div className={"w-48"}>
                                <BlueButton text={"Download"} onClick={download} loading={downloading}/>
                            </div>
                        </div>
                    </div>
                    <div className="mt-2">
                        {loading && (
                            <div className="w-full text-center">Loading...</div>
                        )}
                        {error && (
                            <div className="text-red-500 w-full text-center">Error</div>
                        )}
                        {!loading && !error &&
                            records?.results.length == 0
                            && (
                                <div className="w-full text-center">No results</div>
                            )}
                        {!loading && !error && records?.results.length != 0 &&

                            <>
                                <TableHeader/>
                                {records?.results.map((r, i) => {
                                    return (
                                        <Record key={i} record={r}/>
                                    );
                                })}</>

                        }
                    </div>
                    <div className="absolute bottom-5 right-10">
                        <div className="flex text-light-grey">
                            {records &&
                                records.results.length !== 0 && (
                                    <Pagination
                                        currentPage={page}
                                        itemsPerPage={rpp}
                                        dataLength={records.totalResults ?? 0}
                                        togglePageSelect={(page) => setPage(page)}
                                    />
                                )}
                        </div>
                    </div>
                </div>

            </div>
        </CountryRiskProvider>

    );
}


const FilterBar = () => {
    return <div className="h-full w-1/3 text-light-grey shadowlg bg-white pb-10 max-w-[500px] shadow-md">
        <div className="flex bg-light-grey">
            <div
                className={"py-3 w-full"}
            >
                <div className="text-white text-center w-full">Filter</div>
            </div>
        </div>
    </div>
}

const TableHeader = () => {

    const user = useContext(UserContext);

    let cols = 9
    if (user?.role.auroraAccessLevel == "client") {
        cols = 8
    }
    if (user?.role.auroraAccessLevel == "department") {
        cols = 8
    }


    return (
        <div className="grid gap-x-4">
            <div className={`rounded-t-lg bg-desaturated-grey py-3 grid grid-cols-${cols} w-full items-center h-full`}>
                {["all"].includes(user?.role.auroraAccessLevel || "") &&
                    <div className="font-bold border-r-1 text-center relative h-full flex items-center justify-center">
                        Client
                    </div>
                }

                {["all", "client", "department"].includes(user?.role.auroraAccessLevel || "") &&
                    <div className="font-bold border-r-1 text-center h-full flex items-center justify-center">
                        Department
                    </div>
                }

                <div className="font-bold border-r-1 text-center relative h-full flex items-center justify-center">
                    User
                </div>


                <div className="font-bold border-r-1 text-center relative h-full flex items-center justify-center">
                    Date/Time
                </div>
                <div className="font-bold border-r-1 text-center relative h-full flex items-center justify-center">
                    Country
                </div>
                <div className="font-bold border-r-1 text-center relative h-full flex items-center justify-center">
                    Location
                </div>
                <div className="font-bold border-r-1 text-center relative h-full flex items-center justify-center">
                    Risk Level
                </div>
                <div className="font-bold border-r-1 text-center relative h-full flex items-center justify-center">
                    Type
                </div>
                <div className="font-bold text-center relative h-full flex items-center justify-center  s">
                    Actions
                </div>


            </div>
        </div>
    );
}

type RecordParams = {
    record: Item
}

type ItineraryItemProps = {
    item: Itinerary
}

const RiskLevelIcon: React.FC<{ countryISO?: string, level?: number, large?: boolean }> = ({
                                                                                               countryISO,
                                                                                               level,
                                                                                               large
                                                                                           }) => {

    const {getRisk} = useCountryRisk()
    const risk = level || getRisk(countryISO || "")

    if (!risk) {
        return null
    }

    if (large) {
        return <span
            className={`w-8 h-8 rounded-2xl bg-risk-${risk} bg-risk-1 text-white items-center inline-flex justify-center content-center`}>
    <span>{risk}</span>
  </span>
    }


    return <span
        className={`w-5 h-5 text-xs rounded-2xl bg-risk-${risk} text-white items-center inline-flex justify-center content-center`}>
    <span>{risk}</span>
  </span>

}

export const ItineraryItem: React.FC<ItineraryItemProps> = ({item}) => {

    return (
        <div>
            <div className={"grid grid-cols-[9em,20em,9em,20em] px-8 mt-4"}>
                <div className={"col-span-4 font-bold mb-2"}>
                    Details
                </div>
                <div className={"font-semibold capitalize"}>
                    Type
                </div>

                <div className={"capitalize"}>
                    {item.type}
                </div>

                <div className={"font-semibold capitalize"}>
                    Subtype
                </div>

                <div className={"capitalize"}>
                    {item.subType}
                </div>


            </div>

            <div className={"grid grid-cols-[9em,20em,9em,20em] px-8 mt-4"}>
                <div className={"font-semibold capitalize"}>
                    {item.type == "accommodation" ? "Arrival Time" : "Departure Time"}
                </div>

                <div className={"capitalize"}>
                    {moment.tz(item.startTime, item.locations[0].timezone).format("DD/MM/YYYY HH:mm")} ({item.locations[0].timezone})
                </div>

                <div className={"font-semibold capitalize"}>
                    {item.type == "accommodation" ? "Departure Time" : "Arrival Time"}
                </div>

                <div className={"capitalize"}>
                    {moment.tz(item.endTime, item.locations[item.locations.length - 1].timezone).format("DD/MM/YYYY HH:mm")} ({item.locations[item.locations.length - 1].timezone})
                </div>

            </div>

            <div className={"grid px-8 grid-cols-[9em,20em,9em,20em,9em,20em]"}>


                {Object.keys(item.details).map((k) => <>
                    <div key={`${k}-1`} className={"font-semibold capitalize"}>
                        {startCase(k)}
                    </div>
                    <div key={`${k}-2`} className={""}>
                        {item.details[k] || "–"}
                    </div>
                </>)}
            </div>

            {item.locations.map((l, i) => (<>
                <div key={i} className={"grid px-8 mt-4 grid-cols-[9em,20em,9em,20em,9em,20em]"}>
                    {item.locations.length == 1 && (
                        <div className={"col-span-6 font-bold mb-2"}>
                            Location <RiskLevelIcon countryISO={l.countryISO}/>
                        </div>
                    )}


                    {item.locations.length == 2 && (
                        <div className={"col-span-6 font-bold mb-2"}>
                            {i == 0 ? "Origin Location" : "Destination Location"} <RiskLevelIcon
                            countryISO={l.countryISO}/>
                        </div>
                    )}
                    <div className={"font-semibold capitalize"}>
                        Address
                    </div>

                    <div className={""}>
                        {l.addressLines.join(", ")}
                    </div>

                    <div className={"font-semibold capitalize"}>
                        Town
                    </div>

                    <div className={""}>
                        {l.town}
                    </div>

                    <div className={"font-semibold capitalize"}>
                        County
                    </div>

                    <div className={""}>
                        {l.county}
                    </div>

                    <div className={"font-semibold capitalize"}>
                        Region
                    </div>

                    <div className={""}>
                        {l.region}
                    </div>
                    <div className={"font-semibold capitalize"}>
                        Country
                    </div>

                    <div className={""}>
                        {l.countryName}
                    </div>

                    <div className={"font-semibold capitalize"}>
                        Postal Code
                    </div>

                    <div className={""}>
                        {l.postalCode}
                    </div>
                </div>

            </>))}

            <hr className={"mt-4"}/>


        </div>
    )
}

export type ItineraryItemType = {
    type: string;
    subType: string;
    locations: Array<{
        countryISO: string;
        countryName: string;
        region: string;
        county: string;
        town: string;
        postalCode: string;
        addressLines: Array<string>;
        timezone: string;
        point: { type: string; coordinates: Array<number> };
        formatted: string
    }>;
    startTime: string;
    endTime: string;
    details: Record<string, string>
}

const Record: React.FC<RecordParams> = ({record}) => {

    const [showTrip, setShowTrip] = useState(false)
    const user = useContext(UserContext);


    const navigate = useNavigate()

    const [tripPage, setTripPage] = useState(1)

    const formatSubtypeSource = (raw: string) => {
        switch (raw) {
            case "app_sms":
                return "App SMS";
            case "app":
                return "App";
            case "satellite":
                return "Satellite";
            case "event":
                return "Event";
            case "travel":
                return "Travel";
            case "accommodation":
                return "Accommodation";
            case "trip":
                return "Trip";
            default:
                return raw.replaceAll("_", " ").replace(/\b\w/g, (char) => char.toUpperCase());
        }
    };

    let cols = 9
    if (user?.role.auroraAccessLevel == "client") {
        cols = 8
    }
    if (user?.role.auroraAccessLevel == "department") {
        cols = 8
    }

    const tzContext = useTimeZoneSelectContext()


    return (
        <>

            <div className={`py-3 border-b-1 w-full grid grid-cols-${cols} items-center h-full`}>
                {["all"].includes(user?.role.auroraAccessLevel || "") && <div className="text-center">
                    {record.clientName || "-"}
                </div>}
                {["all", "client", "department"].includes(user?.role.auroraAccessLevel || "") &&
                    <div className="text-center">
                        {record.departmentName || "-"}
                    </div>}
                <div className="text-center">
                    {record.userName || "-"}
                </div>

                {record.startTime == record.endTime && <div className="text-center">
                    {dateToStringTZ(record.endTime, tzContext.timeZone, true)}
                </div>}


                {record.startTime != record.endTime && <div className="text-center">
                    {dateToStringTZ(record.startTime, tzContext.timeZone, true)}
                    <br/>
                    {dateToStringTZ(record.endTime, tzContext.timeZone, true)}
                </div>}

                <div className={"text-center"}>
                    {record.location.countryName}
                </div>
                <div className={"text-center"}>
                    {record.location.town}
                </div>

                <div className={"text-center"}>
                    <RiskLevelIcon level={record.currentCountryRisk} large={true}/>
                </div>


                <div className={"text-center"}>
                    <div>{formatSubtypeSource(record.subtype.split(":")[0])}</div>
                    <div className={"capitalize text-sm"}>{record.subtype.split(":")[1].split("_").join(" ")}</div>

                </div>

                <div className={"px-6"}>
                    <ActionSelect label={"Select Action"}>
                        {record.tripID && record.tripID != "000000000000000000000000" &&
                            <Action label={showTrip ? "Hide Trip" : "Show Trip"}
                                    onSelect={() => setShowTrip(!showTrip)}/>}
                        {record.tripID && record.tripID != "000000000000000000000000" &&
                            <Action label={"Edit Trip"}
                                    onSelect={() => window.open(`/trips/${record.tripID}/edit`, "_blank")}/>}
                    </ActionSelect>
                    {/* {record.trip &&*/}
                    {/*    <BlueButton text={showTrip ? "Hide Trip" : "Show Trip"} onClick={() => setShowTrip(!showTrip)}/>}*/}
                </div>


            </div>


            {showTrip && <div className={"relative"}>
                {record.trip.itinerary[tripPage - 1] && <ItineraryItem item={record.trip.itinerary[tripPage - 1]}/>}

                <Pagination currentPage={tripPage} itemsPerPage={1} dataLength={record.trip.itinerary.length}
                            togglePageSelect={setTripPage}/>
            </div>}
        </>
    )
        ;
}


export default WorldSearch;


type Item = {
    id: string
    type: string
    subtype: string
    location: {
        countryISO: string
        countryName: string
        region: string
        county: string
        town: string
        postalCode: string
        addressLines: Array<string>
        timezone: string
        point: {
            type: string
            coordinates: Array<number>
        }
        formatted: string
        nearestPlace: string
    }
    startTime: string
    endTime: string
    startCountryRisk: number
    endCountryRisk: number
    currentCountryRisk: number
    userID: string
    tripID: string
    userName: string
    departmentName: string
    departmentID: string
    clientName: string
    clientID: string
    trip: {
        id: string
        userID: string
        startTime: string
        endTime: string
        settings: {
            userEditable: boolean
            allowThirdPartyUpdates: boolean
        }
        source: {
            provider: string
            type: string
            id: string
            format: string
            rawData: string
        }
        checkInSettings: {
            types: Array<string>
            applyTo: Array<any>
            times: Array<string>
            days: Array<string>
            gracePeriod: number
            preventDuring: Array<any>
        }
        itinerary: ItineraryItemType[]
        status: {
            valid: boolean
            checkInsGenerated: boolean
            errors: Array<any>
        }
        deleted: boolean
        lastModifiedAt: string
    }
}
